import { useEffect, useState } from "react";
import {
  isTablet,
  isMobile,
  isDesktop,
  isIPad13,
  isIOS,
} from "react-device-detect";

/**
 * Detects if the device is an iPad
 * Uses react-device-detect's built-in detection
 */
export function isIPad(): boolean {
  if (typeof window === "undefined") return false;
  
  // Check for newer iPads (iPadOS 13+)
  if (isIPad13) return true;
  
  // Check for older iPads - iOS tablet
  if (isIOS && isTablet) return true;
  
  // Additional check: iPad in user agent (for older detection)
  const ua = navigator.userAgent.toLowerCase();
  if (/ipad/.test(ua)) return true;
  
  // Newer iPads may report as Mac with touch support
  if (isTablet && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1) {
    const isMac = /macintosh/.test(ua);
    if (isMac) return true;
  }
  
  return false;
}

/**
 * SSR-safe hook for isMobile. Returns false during SSR and initial hydration,
 * then the actual value after mount. Use this when isMobile affects render output
 * to avoid hydration mismatch (react-device-detect differs between server and client).
 */
export function useSafeIsMobile(): boolean {
  const [isMobileSafe, setIsMobileSafe] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setIsMobileSafe(isMobile);
  }, []);

  return isMobileSafe;
}

/**
 * React hook to detect device type using react-device-detect
 * Returns: { isIPad: boolean, isLaptop: boolean, isTablet: boolean, isMobile: boolean }
 */
export function useDeviceType() {
  const [deviceType, setDeviceType] = useState({
    isIPad: false,
    isLaptop: false,
    isTablet: false,
    isMobile: false,
  });

  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined") return;

    const iPad = isIPad();
    const laptop = isDesktop; // react-device-detect's isDesktop covers laptops
    const tablet = isTablet && !iPad; // Other tablets, not iPad
    const mobile = isMobile && !isTablet; // Mobile but not tablet

    setDeviceType({
      isIPad: iPad,
      isLaptop: laptop,
      isTablet: tablet,
      isMobile: mobile,
    });
  }, []);

  return deviceType;
}

